import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { privateRequest } from "services/privateRequest";
import { SECTIONS } from "services/endPoints";

const initialState = {
  sectionsList: [],
  sectionLoading: false,
  createSectionLoading: false,
  updateSectionLoading: false,
  deleteSectionLoading: false,
};

const extractList = (res) => {
  const responseData = res?.data?.data ?? res?.data ?? [];
  if (Array.isArray(responseData)) return responseData;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData?.content)) return responseData.content;
  return [];
};

export const getSectionsByProject = createAsyncThunk(
  "section/getSectionsByProject",
  async (projectId, { rejectWithValue }) => {
    try {
      const res = await privateRequest.get(
        `${SECTIONS}/project/${projectId}`
      );
      return extractList(res);
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const createSection = createAsyncThunk(
  "section/createSection",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await privateRequest.post(SECTIONS, payload);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Section created successfully."
        );
      }
      return res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateSection = createAsyncThunk(
  "section/updateSection",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await privateRequest.put(`${SECTIONS}/${id}`, payload);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Section updated successfully."
        );
      }
      return res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const deleteSection = createAsyncThunk(
  "section/deleteSection",
  async (id, { rejectWithValue }) => {
    try {
      const res = await privateRequest.delete(`${SECTIONS}/${id}`);
      if (res) {
        toast?.success?.(
          res?.data?.message || "Section deleted successfully."
        );
      }
      return { id, data: res?.data };
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const sectionSlice = createSlice({
  name: "section",
  initialState,
  reducers: {
    clearSections: (state) => {
      state.sectionsList = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSectionsByProject.pending, (state) => {
      state.sectionLoading = true;
    });
    builder.addCase(getSectionsByProject.fulfilled, (state, action) => {
      state.sectionLoading = false;
      state.sectionsList = action.payload || [];
    });
    builder.addCase(getSectionsByProject.rejected, (state) => {
      state.sectionLoading = false;
    });

    builder.addCase(createSection.pending, (state) => {
      state.createSectionLoading = true;
    });
    builder.addCase(createSection.fulfilled, (state) => {
      state.createSectionLoading = false;
    });
    builder.addCase(createSection.rejected, (state) => {
      state.createSectionLoading = false;
    });

    builder.addCase(updateSection.pending, (state) => {
      state.updateSectionLoading = true;
    });
    builder.addCase(updateSection.fulfilled, (state) => {
      state.updateSectionLoading = false;
    });
    builder.addCase(updateSection.rejected, (state) => {
      state.updateSectionLoading = false;
    });

    builder.addCase(deleteSection.pending, (state) => {
      state.deleteSectionLoading = true;
    });
    builder.addCase(deleteSection.fulfilled, (state) => {
      state.deleteSectionLoading = false;
    });
    builder.addCase(deleteSection.rejected, (state) => {
      state.deleteSectionLoading = false;
    });
  },
});

export const { clearSections } = sectionSlice.actions;

export default sectionSlice.reducer;
